import { useState, type FormEvent } from "react";
import { Eye, EyeOff, CheckCircle } from "lucide-react";
import { Modal } from "../../components/ui/Modal";
import { LoadingSpinner } from "../../components/ui/LoadingSpinner";
import { changeUserPassword } from "../../lib/auth";

interface ChangePasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function ChangePasswordModal({ isOpen, onClose }: ChangePasswordModalProps) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const reset = () => {
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setShowCurrent(false);
    setShowNew(false);
    setError(null);
    setLoading(false);
    setDone(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (newPassword !== confirmPassword) {
      setError("New passwords do not match.");
      return;
    }
    if (newPassword.length < 8) {
      setError("New password must be at least 8 characters.");
      return;
    }
    if (newPassword === currentPassword) {
      setError("New password must be different from your current password.");
      return;
    }

    setLoading(true);
    try {
      await changeUserPassword(currentPassword, newPassword);
      setDone(true);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed to change password.";
      if (msg.includes("Incorrect username or password") || msg.includes("NotAuthorizedException")) {
        setError("Current password is incorrect.");
      } else if (msg.includes("InvalidPasswordException") || msg.includes("Password does not conform")) {
        setError("New password does not meet requirements. Use at least 8 characters with uppercase, lowercase, and a number.");
      } else if (msg.includes("LimitExceededException") || msg.includes("Attempt limit exceeded")) {
        setError("Too many attempts. Please try again later.");
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Change Password" size="sm">
      {done ? (
        <div className="flex flex-col items-center gap-5 py-4 text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[#f0fdf4]">
            <CheckCircle className="h-7 w-7 text-[#16a34a]" />
          </div>
          <div>
            <p className="text-sm font-semibold text-[#0f172a]">Password changed</p>
            <p className="mt-1 text-sm text-[#64748b]">Your password has been updated successfully.</p>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="w-full rounded-lg bg-[#2447d9] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#1e3a8a] focus:outline-none focus:ring-2 focus:ring-[#2447d9] focus:ring-offset-2 transition-colors"
          >
            Done
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          {error && (
            <div className="flex items-start gap-2.5 rounded-lg bg-[#fef2f2] border border-[#fca5a5] px-4 py-3 text-sm text-[#dc2626]">
              <span className="shrink-0 mt-0.5">⚠</span>
              <span>{error}</span>
            </div>
          )}

          <div className="space-y-1.5">
            <label htmlFor="cp-current" className="block text-sm font-medium text-[#0f172a]">
              Current Password
            </label>
            <div className="relative">
              <input
                id="cp-current"
                type={showCurrent ? "text" : "password"}
                autoComplete="current-password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Enter current password"
                required
                className="w-full rounded-lg border border-[#e2e8f0] bg-[#f8fafc] px-4 py-2.5 pr-11 text-sm text-[#0f172a] placeholder:text-[#94a3b8] transition-colors focus:bg-white focus:border-[#2447d9] focus:outline-none focus:ring-2 focus:ring-[#2447d9]/20"
              />
              <button
                type="button"
                onClick={() => setShowCurrent((v) => !v)}
                tabIndex={-1}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#94a3b8] hover:text-[#64748b] transition-colors"
                aria-label={showCurrent ? "Hide password" : "Show password"}
              >
                {showCurrent ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          <div className="space-y-1.5">
            <label htmlFor="cp-new" className="block text-sm font-medium text-[#0f172a]">
              New Password
            </label>
            <div className="relative">
              <input
                id="cp-new"
                type={showNew ? "text" : "password"}
                autoComplete="new-password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="At least 8 characters"
                required
                className="w-full rounded-lg border border-[#e2e8f0] bg-[#f8fafc] px-4 py-2.5 pr-11 text-sm text-[#0f172a] placeholder:text-[#94a3b8] transition-colors focus:bg-white focus:border-[#2447d9] focus:outline-none focus:ring-2 focus:ring-[#2447d9]/20"
              />
              <button
                type="button"
                onClick={() => setShowNew((v) => !v)}
                tabIndex={-1}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#94a3b8] hover:text-[#64748b] transition-colors"
                aria-label={showNew ? "Hide password" : "Show password"}
              >
                {showNew ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          <div className="space-y-1.5">
            <label htmlFor="cp-confirm" className="block text-sm font-medium text-[#0f172a]">
              Confirm New Password
            </label>
            <input
              id="cp-confirm"
              type="password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Re-enter new password"
              required
              className="w-full rounded-lg border border-[#e2e8f0] bg-[#f8fafc] px-4 py-2.5 text-sm text-[#0f172a] placeholder:text-[#94a3b8] transition-colors focus:bg-white focus:border-[#2447d9] focus:outline-none focus:ring-2 focus:ring-[#2447d9]/20"
            />
          </div>

          <div className="flex gap-3 pt-1">
            <button
              type="button"
              onClick={handleClose}
              disabled={loading}
              className="flex-1 rounded-lg border border-[#e2e8f0] px-4 py-2.5 text-sm font-medium text-[#64748b] hover:bg-[#f8fafc] transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!currentPassword || !newPassword || !confirmPassword || loading}
              className="flex-1 flex items-center justify-center gap-2 rounded-lg bg-[#2447d9] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#1e3a8a] focus:outline-none focus:ring-2 focus:ring-[#2447d9] focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <LoadingSpinner size="sm" className="text-white" />
                  <span>Updating…</span>
                </>
              ) : (
                "Update Password"
              )}
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
}
